import c from 'classnames';
import dynamic from 'next/dynamic';
import useTranslation from 'next-translate/useTranslation';
import { NextSeo } from 'next-seo';
import { startOfDay } from 'date-fns';

import { Grid, Aside, Eyebrow } from '@keahotels/components';

// import { ReactComponent as Breakfast } from '@keahotels/assets/icons/Breakfast.svg';
// import { ReactComponent as Check } from '@keahotels/assets/icons/Check.svg';
// import { ReactComponent as NoRefund } from '@keahotels/assets/icons/NoRefund.svg';
// import { ReactComponent as Parking } from '@keahotels/assets/icons/Parking.svg';
// import { ReactComponent as Wifi } from '@keahotels/assets/icons/Wifi.svg';

import HotelBookingToolbar from '../../components/HotelBookingToolbar/HotelBookingToolbar';

import styles from './PageBookingSearch.module.css';

import { useSettings } from '../../components/AppLayout/AppLayout';
import HotelFallbackHeader from '../../components/HotelFallbackHeader/HotelFallbackHeader';
import { useRouter } from 'next/router';
import { BookingSearchQuery } from '../../components/BookingSearchResults/useBookingSearch';
import BookingResultError from '../../components/BookingSearchResults/BookingResultError';

const BookingSearchResults = dynamic(
	() => import('../../components/BookingSearchResults/BookingSearchResults'),
	{
		ssr: false,
	},
);

const FallbackHotelSearch = dynamic(() => import('./FallbackHotelSearch'), {
	ssr: false,
});

export type PageBookingSearchProps = {};

export default function PageBookingSearch({
	isFallback = false,
	fallbackTitle,
	fallbackEyebrow,
}: {
	isFallback?: boolean;
	fallbackTitle?: string | null;
	fallbackEyebrow?: string | null;
}) {
	const { query } = useRouter();
	const { from, to } = query as BookingSearchQuery;

	const { hotelSettings } = useSettings();
	const { t } = useTranslation('common');

	const intro = hotelSettings?.intro;
	if (!hotelSettings && isFallback) return null;

	const today = startOfDay(new Date());
	let dateError = '';
	if (!from || !to) {
		dateError = 'dateError.dateUndefined';
	} else if (from >= to) {
		dateError = 'dateError.fromAfterTo';
	} else if (new Date(from) < today) {
		dateError = 'dateError.fromBeforeToday';
	}

	return (
		<>
			{!isFallback && (
				<>
					<NextSeo
						title={`${hotelSettings?.title}`}
						description={intro || undefined}
						additionalMetaTags={[
							{
								name: 'robots',
								content: 'noindex',
							},
						]}
					/>
					<HotelBookingToolbar />
				</>
			)}

			<Grid className={styles.wrap}>
				{isFallback && (
					<HotelFallbackHeader
						title={fallbackTitle}
						eyebrow={fallbackEyebrow}
					/>
				)}
				<div
					className={c({
						[styles.main]: 1,
						[styles.content]: 1,
						[styles.topBorder]: !isFallback,
					})}
				>
					{/* <VisuallyHidden as="h1">
						5 rooms available at Sand Hotel
					</VisuallyHidden> */}

					{/* <ButtonGroup
						aria-label={t('filterResults')}
						style={{ marginBottom: 80 }}
					>
						<Button variant="tab" aria-selected size="small">
							{t('ourSuggestions')}
						</Button>
						<Button variant="tab" size="small">
							{t('manualSelection')}
						</Button>
					</ButtonGroup> */}

					{!dateError && <BookingSearchResults />}

					<BookingResultError hasError={!!dateError}>
						{t(dateError)}
					</BookingResultError>
				</div>

				<Aside
					left
					className={c({
						[styles.aside]: 1,
						[styles.topBorder]: !isFallback,
					})}
					innerClassName={styles.innerAside}
				>
					{intro && (
						<div
							className={c({
								[styles.asideText]: 1,
								[styles.topBorder]: isFallback,
							})}
							dangerouslySetInnerHTML={{ __html: intro }}
						/>
					)}
					{hotelSettings?.checkIn && hotelSettings.checkOut && (
						<dl
							className={`${styles.asideSection} ${styles.checkInOut}`}
						>
							<div className={styles.checkInOutItem}>
								<Eyebrow as="dt">{t('check-in')}</Eyebrow>
								<dd>{hotelSettings.checkIn}</dd>
							</div>
							<div className={styles.checkInOutItem}>
								<Eyebrow as="dt">{t('check-out')}</Eyebrow>
								<dd>{hotelSettings.checkOut}</dd>
							</div>
						</dl>
					)}
					{/* <dl className={styles.asideSection}>
						<Eyebrow as="dt">Amenities</Eyebrow>
						<IconListItem as="dd" icon={<Wifi />}>
							WiFi
						</IconListItem>
						<IconListItem as="dd" icon={<Breakfast />}>
							Breakfast
						</IconListItem>
						<IconListItem as="dd" icon={<NoRefund />}>
							Non refundable
						</IconListItem>
						<IconListItem as="dd" icon={<Check />}>
							VAT included
						</IconListItem>
						<IconListItem as="dd" icon={<Parking />}>
							Parking
						</IconListItem>
					</dl> */}
				</Aside>
			</Grid>
			{!isFallback &&
				!dateError &&
				hotelSettings?.fallbackHotels.map(
					({ title, eyebrow, hotel }) => (
						<FallbackHotelSearch
							key={hotel.slug}
							title={title}
							eyebrow={eyebrow}
							hotelSlug={hotel.slug}
						/>
					),
				)}
		</>
	);
}
